import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, Loader2, Check, AlertCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { sessionService } from '../../api/services';

const POLL_MS = 3000;

// Déballe une réponse wrappée { success, data } ou brute.
const unwrap = (body) =>
  body && body.success !== undefined && body.data !== undefined ? body.data : body;

const formatCountdown = (s) => {
  if (s <= 0) return '0s';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `${sec}s`;
};

/**
 * Affiche un QR code WhatsApp qui se rafraîchit automatiquement.
 * Gère l'expiration du cycle et le bouton « Régénérer le QR ».
 *
 * @param {string}   sessionId  - ID de session WhatsFlow (whatsapp.sessionId de l'agent)
 * @param {Function} onConnected - appelé quand la session passe « connected »
 */
export default function WhatsAppQRConnect({ sessionId, onConnected }) {
  // phase : 'loading' | 'qr_ready' | 'expired' | 'restarting' | 'connected' | 'error'
  const [phase, setPhase] = useState('loading');
  const [qrCode, setQrCode] = useState(null);
  const [expiresIn, setExpiresIn] = useState(null);
  const [qrFlash, setQrFlash] = useState(false); // bref flash visuel lors d'un nouveau QR
  const [errorMsg, setErrorMsg] = useState('');

  const pollRef = useRef(null);
  const countdownRef = useRef(null);
  const mountedRef = useRef(true);
  const prevQrRef = useRef(null); // ref pour éviter closure stale dans setInterval

  const clearAll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearAll();
    };
  }, [clearAll]);

  // Lance un compte à rebours local (reset si un nouveau QR arrive).
  const startCountdown = useCallback((seconds) => {
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    setExpiresIn(Math.max(0, seconds));
    countdownRef.current = setInterval(() => {
      setExpiresIn((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          if (mountedRef.current) setPhase('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const pollQR = useCallback(async () => {
    if (!sessionId || !mountedRef.current) return;
    try {
      const data = unwrap(await sessionService.getQR(sessionId));
      if (!mountedRef.current) return;

      if (data.status === 'connected') {
        clearAll();
        setPhase('connected');
        // Fetch phone number before calling onConnected
        let phone = null;
        try {
          const statusData = unwrap(await sessionService.getSessionStatus(sessionId));
          phone = statusData?.phone_number || statusData?.phoneNumber || null;
        } catch (_) {}
        setTimeout(() => { if (mountedRef.current && onConnected) onConnected(phone); }, 800);
        return;
      }

      if (data.status === 'qr_ready' && data.qr_code) {
        // Nouveau QR détecté via ref (évite closure stale)
        if (data.qr_code !== prevQrRef.current) {
          prevQrRef.current = data.qr_code;
          setQrCode(data.qr_code);
          // Flash de 700 ms pour signaler le rafraîchissement
          setQrFlash(true);
          setTimeout(() => { if (mountedRef.current) setQrFlash(false); }, 700);
          // Réinitialiser le compte à rebours avec la valeur serveur
          if (data.expires_in > 0) startCountdown(data.expires_in);
        }
        setPhase('qr_ready');
        return;
      }
      // status === 'starting' → rester en loading
    } catch (err) {
      if (!mountedRef.current) return;
      if (err?.status === 410) { clearAll(); setPhase('expired'); }
      // Autres erreurs transitoires : on retente au prochain tick
    }
  }, [sessionId, clearAll, startCountdown, onConnected]);

  // (Re)démarre le polling quand sessionId change.
  useEffect(() => {
    if (!sessionId) return;
    clearAll();
    prevQrRef.current = null;
    setPhase('loading');
    setQrCode(null);
    setExpiresIn(null);
    pollQR();
    pollRef.current = setInterval(pollQR, POLL_MS);
    return clearAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]); // pollQR exclu intentionnellement — stable via refs

  const handleRegenerate = useCallback(async () => {
    if (!sessionId || !mountedRef.current) return;
    clearAll();
    prevQrRef.current = null;
    setQrCode(null);
    setExpiresIn(null);
    setPhase('restarting');
    setErrorMsg('');
    try {
      await sessionService.restartSession(sessionId);
      if (!mountedRef.current) return;
      setPhase('loading');
      // Laisser ~3 s au bridge pour démarrer Baileys avant de poller
      setTimeout(() => {
        if (!mountedRef.current) return;
        pollQR();
        pollRef.current = setInterval(pollQR, POLL_MS);
      }, 3000);
    } catch (err) {
      if (!mountedRef.current) return;
      if (err?.status === 409) {
        // La session est déjà connectée
        clearAll();
        setPhase('connected');
        if (onConnected) onConnected();
        return;
      }
      setErrorMsg(err?.message || 'Impossible de régénérer le QR. Réessayez.');
      setPhase('error');
    }
  }, [sessionId, clearAll, pollQR, onConnected]);

  // ── Rendu ──────────────────────────────────────────────────────────────────

  if (phase === 'connected') {
    return (
      <div className="text-center py-6">
        <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
          <Check className="text-white" size={28} />
        </div>
        <p className="text-white font-semibold">WhatsApp connecté ✅</p>
        <p className="text-text-gray text-sm mt-1">Votre agent est prêt à répondre.</p>
      </div>
    );
  }

  if (phase === 'expired') {
    return (
      <div className="text-center py-6">
        <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="text-amber-400" size={28} />
        </div>
        <h4 className="text-white font-semibold mb-1">QR Code expiré</h4>
        <p className="text-text-gray text-sm mb-5">
          Le cycle de connexion s'est terminé sans scan. Lancez un nouveau cycle pour obtenir un QR scannable.
        </p>
        <button
          onClick={handleRegenerate}
          className="px-6 py-3 bg-accent-green text-black rounded-lg font-bold hover:bg-accent-green/80 transition-all inline-flex items-center gap-2"
        >
          <RefreshCw size={18} />
          Régénérer le QR
        </button>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="text-center py-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-5 flex items-center gap-2">
          <AlertCircle className="text-red-400 flex-shrink-0" size={16} />
          <p className="text-red-400 text-sm text-left">{errorMsg}</p>
        </div>
        <button
          onClick={handleRegenerate}
          className="px-6 py-3 bg-accent-green text-black rounded-lg font-bold hover:bg-accent-green/80 transition-all inline-flex items-center gap-2"
        >
          <RefreshCw size={18} />
          Réessayer
        </button>
      </div>
    );
  }

  if (phase === 'loading' || phase === 'restarting') {
    return (
      <div className="text-center py-8">
        <Loader2 className="text-accent-green animate-spin mx-auto mb-4" size={40} />
        <p className="text-white font-semibold mb-1">
          {phase === 'restarting' ? 'Redémarrage en cours…' : 'Génération du QR…'}
        </p>
        <p className="text-text-gray text-sm">Prêt dans quelques secondes.</p>
      </div>
    );
  }

  // phase === 'qr_ready'
  return (
    <div className="text-center">
      {/* QR image avec flash de rafraîchissement */}
      <div
        className={`bg-white p-4 rounded-xl inline-block mb-3 transition-all duration-300 ${
          qrFlash ? 'ring-4 ring-accent-green scale-[1.01]' : ''
        }`}
      >
        {qrCode ? (
          <QRCodeSVG
            value={qrCode}
            size={224}
            level="M"
            includeMargin={false}
          />
        ) : (
          <div className="w-56 h-56 bg-gray-100 rounded flex items-center justify-center">
            <Loader2 className="text-gray-400 animate-spin" size={32} />
          </div>
        )}
      </div>

      {/* Compte à rebours */}
      <div className="mb-4 min-h-[24px]">
        {expiresIn !== null && expiresIn > 0 ? (
          <p className="text-text-gray text-sm">
            QR valide encore{' '}
            <span
              className={`font-semibold font-mono transition-colors ${
                expiresIn <= 30 ? 'text-amber-400' : 'text-accent-green'
              }`}
            >
              {formatCountdown(expiresIn)}
            </span>
            {qrFlash && (
              <span className="ml-2 text-accent-green text-xs animate-pulse">
                ↻ nouveau QR
              </span>
            )}
          </p>
        ) : (
          <p className="text-amber-400 text-sm">Expiration imminente…</p>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-primary-card rounded-lg p-4 mb-4 text-left">
        <h5 className="text-white font-semibold mb-3 text-sm">Comment scanner le QR code ?</h5>
        <ol className="space-y-2 text-sm text-text-gray">
          {[
            'Ouvrez WhatsApp sur votre téléphone',
            'Menu (⋮) → Appareils liés → Lier un appareil',
            'Pointez votre téléphone vers cet écran pour scanner',
          ].map((step, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-accent-green font-bold flex-shrink-0">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="flex items-center justify-center gap-2 text-text-gray text-xs">
        <Loader2 size={12} className="animate-spin" />
        En attente du scan — le QR se rafraîchit automatiquement
      </div>
    </div>
  );
}
